import * as React from 'react'
import { CheckIcon } from '@repo/studio-design-system-ui-icons/general'
import { tw } from 'utils/classnames'

/**
 * Small presentational helpers shared across the landing-page sections. They
 * exist so individual sections compose consistent chrome (eyebrows, pills,
 * check rows, icon tiles) without each re-deriving the same Tailwind strings.
 *
 * For faithful reproduction of the marketing mockup these use the bespoke `sd-`
 * palette and arbitrary display values rather than the studio scale tokens.
 * That deviation is intentional and contained to this app.
 *
 * `Pill` is a non-interactive label, so it's a `<span>` rather than the Studio
 * `Tag` (which renders a `<button>`); real CTAs use the download buttons.
 */

/** Centered max-width content column used by every full-bleed section. */
export function SectionContainer({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={tw('mx-auto w-full max-w-[1120px] px-5 md:px-10', className)}>{children}</div>
  )
}

/** Uppercase monospace purple kicker above section headings. */
export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-mono text-[13px] font-semibold uppercase tracking-[0.1em] text-sd-purple">
      {children}
    </p>
  )
}

/** Rounded pastel label chip with an optional leading icon. */
export function Pill({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={tw(
        'inline-flex items-center gap-[9px] rounded-full border border-sd-lilac-border bg-sd-lilac px-[15px] py-[7px]',
        'text-[13px] font-semibold text-sd-purple-deep',
        '[&_svg]:size-[14px] [&_svg]:shrink-0 [&_svg]:text-sd-purple',
        className,
      )}
    >
      {children}
    </span>
  )
}

/** Green check + label, used for the "no card / no subscription" reassurances. */
export function CheckRow({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <span
      className={tw(
        'inline-flex items-center gap-[7px] text-[13px] font-medium text-sd-green',
        className,
      )}
    >
      <CheckIcon className="size-[15px] shrink-0 text-sd-green" aria-hidden />
      {children}
    </span>
  )
}

/**
 * Rounded square icon badge. Default is the 58px trust-pillar tile; pass a
 * `size-*` (and the accent bg/text) via `className` to override — `tw`
 * (tailwind-merge) keeps the last conflicting utility.
 */
export function IconTile({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <span
      className={tw(
        'flex size-[58px] items-center justify-center rounded-[16px] [&_svg]:size-7',
        className,
      )}
    >
      {children}
    </span>
  )
}
